export type ErrorSerialized = {
  message: string;
  clientMessage?: string;
};

type SentryOptions = {
  fingerprint?: string[];
  tags?: Record<string, string | number | boolean>;
};

type SentryTagsKey = "service";
type SentryService = "pld" | "sipsim" | "allianzTrade" | "airtable" | "notification" | "docuseal";
type SentryTagValue = string | number | boolean;
export type SentryTags = Record<SentryTagsKey, SentryService> & Record<string, SentryTagValue>;

export interface SharedErrorOptions {
  status?: number;
  context?: Record<PropertyKey, unknown>;
  clientMessage?: string;
  tags?: SentryTags;
  fingerprint?: string[];
}

/**
 * Options for configuring a SharedError.
 *
 * @property message - Error message. Used for internal logging.
 * @property status - HTML status code sent to client. Defaults to 500.
 * @property context - Context to be sent to Sentry.
 * @property sentry - Sentry configuration. Defaults to undefined. Allows ignoring the error or setting a fingerprint.
 * @property clientMessage - Message to be sent to the client. Defaults to "An error has occurred. Please try again later.".
 */
export class SharedError extends Error {
  public status?: number;
  public clientMessage?: string;
  public context?: Record<PropertyKey, unknown>;
  public sentry?: SentryOptions;

  constructor(
    message: string,
    { status, context, clientMessage, tags, fingerprint }: SharedErrorOptions = {
      status: 500,
      clientMessage: "An error has occurred. Please try again later.",
    }
  ) {
    const actualTags = tags ? { tags } : {};
    const actualFingerprint = fingerprint && fingerprint.length > 0 ? { fingerprint } : undefined;

    super(message);
    this.status = status;
    this.context = context;
    this.clientMessage = clientMessage;
    this.sentry = tags || fingerprint ? { ...actualTags, ...actualFingerprint } : undefined;
  }
}

export function getErrorMessageForClient(error: unknown) {
  let message = "Unknown error";

  // First is for SerializedError
  if (
    error !== null &&
    typeof error === "object" &&
    "clientMessage" in error &&
    typeof error.clientMessage === "string"
  ) {
    message = error.clientMessage;
  }
  // Second is for SerializedError, Error, etc.
  else if (error !== null && typeof error === "object" && "message" in error && typeof error.message === "string") {
    message = error.message;
  } else if (typeof error === "string") {
    message = error;
  }

  return message;
}

export const isAbortError = (error: any) => {
  return typeof error === "object" && !!error && "message" in error && error.message.toLowerCase().includes("aborted");
};
